"use client"

import { useEffect, useState } from "react"
import { usePathname } from "next/navigation"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Logo from "@modules/layout/components/logo"
import SideMenu from "@modules/layout/components/side-menu"
import TraderMenuNav from "@modules/trader-menu/components/trader-menu-nav"
import {
  isPostPurchasePath,
  toChromeActions,
  type ChromeActionVariant,
} from "@modules/layout/lib/chrome-actions-view-model"

// Nav global do chrome Virtual Europe (Story 2.7 / DESIGN.md): transparente no topo, ganha
// vidro ao rolar >20px. Client por causa do scroll. Escopo restrito ⇒ só links de destinos
// REAIS (a landing — Recursos/Brokers/Suporte — é story futura). Sem cart no nav: o funil VE
// é plano→checkout direto. Hambúrguer só <768px (md: = 768, breakpoint que alinha ao DESIGN).
const NAV_SCROLL_THRESHOLD = 20

// `primary` é o `button-primary` do DESIGN.md (blue-600 → hover blue-500) — o fill do chrome,
// deliberadamente distinto dos CTAs de conteúdo (accent-strong).
const ACTION_CLASSES: Record<ChromeActionVariant, string> = {
  link: "px-2 py-2 text-sm font-medium text-text transition-colors hover:text-accent",
  primary:
    "rounded-[8px] bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-500",
}

type NavProps = {
  isAuthenticated: boolean
}

export default function Nav({ isAuthenticated }: NavProps): React.JSX.Element {
  const [scrolled, setScrolled] = useState(false)
  const pathname = usePathname()
  // Story 3.8 — não vender a quem acabou de comprar (ecrã de confirmação).
  const actions = toChromeActions(isAuthenticated, {
    isPostPurchase: isPostPurchasePath(pathname),
  })

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > NAV_SCROLL_THRESHOLD)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <div className="sticky top-0 inset-x-0 z-50">
      <header
        className={`relative h-16 mx-auto border-b transition-all duration-300 motion-reduce:transition-none ${
          scrolled
            ? "border-white/5 bg-white/[0.03] shadow-[0_8px_32px_rgba(0,0,0,0.5)] backdrop-blur-xl"
            : "border-transparent"
        }`}
      >
        <nav className="content-container flex h-full w-full items-center justify-between">
          <Logo />

          <div className="hidden items-center gap-x-8 text-sm font-medium md:flex">
            <LocalizedClientLink
              href="/"
              className="text-white/60 transition-colors hover:text-white"
            >
              Início
            </LocalizedClientLink>
            <LocalizedClientLink
              href="/#planos"
              className="text-white/60 transition-colors hover:text-white"
            >
              Planos
            </LocalizedClientLink>
            {/* Story 10.7 — dropdown "Menu do Trader" (as 5 páginas-wrapper), a par de Início/Planos. */}
            <TraderMenuNav />
          </div>

          <div className="hidden items-center gap-x-3 md:flex">
            {actions.map((action) => (
              <LocalizedClientLink
                key={action.key}
                href={action.href}
                data-testid={`nav-${action.key}-link`}
                className={ACTION_CLASSES[action.variant]}
              >
                {action.label}
              </LocalizedClientLink>
            ))}
          </div>

          {/* Issue #26 — `h-full`: sem altura aqui, toda a cadeia `h-full` do SideMenu colapsava
              e o hambúrguer ficava do tamanho do SVG (24px). O botão também ganhou `min-h-11`
              próprio, para não voltar a depender de um ancestral. */}
          <div className="md:hidden h-full">
            <SideMenu isAuthenticated={isAuthenticated} />
          </div>
        </nav>
      </header>
    </div>
  )
}
